import HighlightRow from '#src/components/HighlightRow';
import ContentRow from '#src/components/ContentRow';
import { From, To } from '#src/components/Responsive';
import {
  selectFields,
  selectContentType,
} from '#src/lib/contentful/selectors.mjs';

import { filterGridComponents, filterFloatingComponents } from './helpers';

const gridAlignMap = {
  left: 'start',
  center: 'center',
  right: 'end',
};

/**
 * A callout section that can be heavily customized and contain multiple
 * components. The components will always be laid out as a grid with single
 * full width rows.
 *
 * @param {object} props - The props!
 * @param {string} [props.backgroundColor='transparent'] - The background color
 *   to apply to this callout section.
 * @param {Array} [props.components=[]] - The components to render in this
 *   callout section.
 * @param {string} [props.mobilePadding] - The padding to apply to this
 *   container for mobile layouts.
 * @param {string} [props.padding='100px 70px'] - The padding to apply to this
 *   container for desktop layouts. Will be used as a fallback if mobilePadding
 *   is omitted.
 * @param {number} [props.elementSpacing=50] - The spacing to apply between
 *   the components.
 * @param {number} [props.mobileElementSpacing=props.elementSpacing] - The spacing to apply between
 *   the components.
 * @param {string} [props.maxContentWidth='100%'] - The maximum width for the
 *   components that are being rendered. This is applied uniformly to every
 *   component that is part of this callout section. This only applies to
 *   desktop layouts. Mobile layouts always have a maximum of 100%.
 * @param {string} [props.alignContent='center'] - How to align the content
 *   inside of the container on desktop layouts. Can be one of 'left', 'center',
 *   or 'right'.
 * @param {string} [props.mobileAlignContent] - How to align the content inside
 *   of the container on mobile layouts. Accepts the same values as alignContent
 *   and will fall back to the alignContent value if omitted.
 * @param {string} [props.className] - The class name to apply to the container
 * @param {string} [props.theme] - The theme to apply to this layout.
 * @param {string} [props.desktopLayout] - Preferred layout orientation
 * @returns {JSX.Element}
 */
function ComposableCallout({
  backgroundColor = 'transparent',
  components = [],
  mobilePadding,
  padding = '100px 70px',
  elementSpacing = 50,
  mobileElementSpacing = elementSpacing,
  maxContentWidth = '100%',
  alignContent = 'center',
  mobileAlignContent,
  className,
  theme,
  desktopLayout,
}) {
  const baseStyle = {
    display: 'grid',
    gap: `${elementSpacing}px`,
    justifyItems: gridAlignMap[alignContent],
    alignItems: 'center',
    padding,
    width: '100%',
  };
  const desktopLayoutStyles = {
    horizontal: {
      display: 'flex',
      justifyContent: 'center',
    },
  };
  const mobileStyle = {
    ...baseStyle,
    gap: `${mobileElementSpacing}px`,
    padding: mobilePadding || padding,
    justifyItems: gridAlignMap[mobileAlignContent || alignContent],
  };
  const gridComponents = filterGridComponents(components);
  const floatingComponents = filterFloatingComponents(components);

  return (
    <HighlightRow background={backgroundColor} className={className}>
      {floatingComponents.map((component, index) => (
        <ContentRow
          key={index}
          theme={theme}
          contentTypeId={selectContentType(component)}
          style={{ maxWidth: maxContentWidth }}
          {...selectFields(component)}
        />
      ))}
      <To slice={2} style={mobileStyle}>
        {gridComponents.map((component, index) => (
          <ContentRow
            key={index}
            theme={theme}
            contentTypeId={selectContentType(component)}
            style={{ maxWidth: '100%' }}
            {...selectFields(component)}
          />
        ))}
      </To>
      <From
        slice={3}
        style={{ ...baseStyle, ...desktopLayoutStyles[desktopLayout] }}
      >
        {gridComponents.map((component, index) => (
          <ContentRow
            key={index}
            theme={theme}
            contentTypeId={selectContentType(component)}
            style={{ maxWidth: maxContentWidth }}
            {...selectFields(component)}
          />
        ))}
      </From>
    </HighlightRow>
  );
}

export default ComposableCallout;
